import React, { useState, useEffect, useMemo } from 'react';
import axios, { CancelTokenSource } from 'axios';
import FormFieldContext from 'components/forms/FormFieldContext';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from './ModalContext';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { handleSaveErrors } from 'services/api/handleSaveErrors';
import { useSubcontractorDayPassCheck } from 'services/subcontractors/useSubcontractorDayPassCheck';
import { formValidation } from 'services/utils/formValidation';
import { shakeElement } from 'services/utils/shakeElement';
import { FieldOption, FieldOptionFetchResponse, FieldType } from 'types/FieldTypes';
import { generateFieldComponents } from 'components/forms/GenerateFieldComponents';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { fetchDropdownDataFromFields } from 'services/api/fetchFieldData';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { sanitizeTextareaFromDataObject } from 'services/utils/convertTextareaData';
import { useHistory } from 'react-router-dom';
import IconButton from 'components/buttons/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

interface FormModalProps {
    viewKey?: string, 
    apiObject: string, 
    fields: FieldType[], 
    linkedItem?: { [key: string]: string | number | number[] };
    redirectTo?: {
        url: string;
        itemIdField: string;
    },
    primaryButtonLabel?: string;
    showDeleteButton?: boolean;
    deleteConfig?: {
        apiUrl: string; // Endpoint name the item id is appended to, used instead of the default patch endpoint
        data?: Record<string, any>; // Payload sent to the delete endpoint, defaults to { deleted: true }
        tooltipText?: string; // Overrides the default delete button tooltip
    };
    sidePanel?: React.ReactNode; // Optional panel which is shown as a side tab next to the form modal
    extraLeftButtons?: React.ReactNode; // Optional buttons shown on the left side of the button row
    onBeforeSubmit?: (dataToSubmit: Record<string, any>) => Promise<boolean>; // Optional async check before saving, false aborts the submit
    onSuccess?: (response?: Record<string, any>) => void
}

const FormModal: React.FC<FormModalProps> = ({
    viewKey, apiObject, fields, linkedItem, redirectTo, primaryButtonLabel, showDeleteButton = false, deleteConfig, sidePanel, extraLeftButtons, onBeforeSubmit, onSuccess
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert, errorMessages, setErrorMessages } = useGlobalContext();
    const { handleLogout, activeFeatures } = useAuthContext();
    const { modalStack, revealModal, closeModal } = useModal();
    const { handleDayPassError } = useSubcontractorDayPassCheck();
    const history = useHistory();
    const [cancelSource, setCancelSource] = useState<CancelTokenSource | null>(null);    
    const [dropdownData, setDropdownData] = useState<Record<string, FieldOptionFetchResponse>>({});
    const [groupOptions, setGroupOptions] = useState<FieldOption[]>([]);
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [formData, setFormData] = useState<any>([]);
    // const [formData, setFormData] = useState<FieldType[]>([]);
    const [formDataLoaded, setFormDataLoaded] = useState(false);
    const [editing, setEditing] = useState(true);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const topModal = modalStack[modalStack.length - 1];
    const itemId = topModal?.props?.itemId;

    // Fetch the current data and dropdown options
    useEffect(() => {
        // Fetch the current data of the form
        const fetchFieldData = async () => {
            if (itemId) {
                try {
                    const response = await fetchData({ apiObject, itemId, handleLogout });
                    setFormData(response);
                } finally {
                    setFormDataLoaded(true);
                }
            } else {
                setFormDataLoaded(true);
            }
        };

        // Fetch options of dropdown and (multi)select fields
        const fetchDropdownData = async () => {
            const dropdownData: Record<string, FieldOptionFetchResponse> = {};
            for (let field of fields) {

                // Don't execute fetches for fields which are not shown
                if ('activeFeature' in field && field.activeFeature) {
                    if (!activeFeatures.includes(field.activeFeature)) {
                        continue;
                    }
                }

                // Fetch by api request object
                if ((field.type === 'dropdown' || field.type === 'job-workflow-default-tasks') && field.apiRequest) {

                    // Get the request config
                    const request = field.apiRequest[0];

                    // Fetch the data
                    const results = await fetchData({ 
                        apiUrl: request.endpoint,
                        params: request.params,
                        handleLogout 
                    });

                    // Store the results
                    dropdownData[request.object] = results;
                }
                
                // Fetch by api object prop
                if ((field.type === 'multiselect' || 
                     field.type === 'tabs' || 
                     field.type === 'price' || 
                     field.type === 'price-currency' || 
                     field.type === 'scheduling-board-resources'
                    ) && field.apiObject
                ) {
                    const results = await fetchDropdownDataFromFields(field.apiObject, field.params, handleLogout);
                    dropdownData[field.apiObject] = results;
                }
                
                // If multiselect fields are grouped, fetch the group data
                if (field.type === 'multiselect' && field.groupByField) {
                    const results = await fetchData({ apiObject: field.groupByField?.apiObject, params: { is_active: 'true' }, handleLogout });
                    const fieldName = field.groupByField.apiField;

                    // Only extract the id and the given field name from the data
                    const extractedFields = results.results.map((item: any) => ({
                        id: item.id,
                        [fieldName]: item[fieldName],
                    }));
                    setGroupOptions(extractedFields);
                }
            }
            setDropdownData(dropdownData);
        }

        // Execute the fetches
        (async () => {
            try {
                await Promise.all([fetchFieldData(), fetchDropdownData()]);
            } finally {
                // Reveal the modal after loading
                revealModal();
            }
        })();
    }, []);

    // Handle submit
    const handleSubmit = async () => {
        // Configure the data to submit
        let dataToSubmit: Record<string, any> = { 
            ...updatedData, 
            ...(itemId ? { id: itemId } : {}),

            // Add the linked item if given by the form config
            ...(linkedItem ? linkedItem : {})
        };

        // Perform form validation on submit
        for (const field of fields) {
            if ('name' in field && field.name !== undefined) {
                const fieldName = field.name;
                if (fieldName && fieldName in dataToSubmit) {
                    const validationError = formValidation(field, dataToSubmit);
                    if (validationError) {
                        setErrorMessages(validationError);
                        return;
                    }
                }
            }   
        }

        // If there are textarea fields, convert and sanitize the text
        dataToSubmit = sanitizeTextareaFromDataObject(dataToSubmit, fields);

        // Run the optional check before saving, for example the subcontractor day passes
        if (onBeforeSubmit) {
            setButtonLoader(true);
            const mayContinue = await onBeforeSubmit(dataToSubmit);
            setButtonLoader(false);
            if (!mayContinue) return;
        }

        try {
            setButtonLoader(true);

            // Create the cancel token source
            const source = axios.CancelToken.source();
            setCancelSource(source);

            // Saves the data
            const response = await saveData({ apiObject, itemId, data: dataToSubmit, source });

            // Handle successful response
            if (response?.status === 200 || response?.status === 201) {
                setButtonLoader(false);
                setFloatingAlert({ 'type': 'success' })

                // Close the form modal before the callbacks so a callback
                // which opens a follow up modal is not closed right away
                closeModal();

                // Call the on success callbacks
                if (topModal?.props?.onSuccess) topModal.props.onSuccess(response.data);

                // Call the form modal success callback
                if (onSuccess) onSuccess(response.data);

                // Handle optional redirect
                if (redirectTo) {
                    // Get the item id of the newly created item
                    const newItemId = response.data[redirectTo.itemIdField];

                    // Redirect page
                    history.push(`/${redirectTo.url}/${newItemId}`);
                };

                setErrorMessages({});
            }
        } catch (error) {
            // Show the day pass modal when the backend refused the save for missing day passes
            if (handleDayPassError(error)) {
                setButtonLoader(false);
                return;
            }

            // Extract the names of the fields from the fields to do validation on
            const fieldsWithName = fields.filter(field => 'name' in field);
            const errorFields = (fieldsWithName as Array<{ name: string }>).map(field => field.name);

            // Handle the errors using the handleSaveErrors function
            const errorData = handleSaveErrors(error, errorFields)
            setButtonLoader(false);
            setErrorMessages(errorData);

            // Shake the modal in any case of an error
            if (topModal?.modalRef) shakeElement(topModal.modalRef.current as HTMLElement);
        }
    };

    // Closes the modal when clicking the close or cancel button
    const handleClose = () => {
        // Removes the current data, so the form will be empty at reopening
        setFormData([]);

        // Removes errors from fields, hides error alert and closes the modal
        setErrorMessages({});
        setShowErrorAlert(false);
        closeModal();

        // If a request is still pending when closing, cancel the request
        if (cancelSource) {
            cancelSource.cancel();
        }

        // Stops the button loader icon
        setButtonLoader(false);
    }

    // Deletes the item and closes the modal
    const handleDelete = async () => {
        try {
            // Start loader
            setDeleteLoader(true);

            // Create the cancel token source
            const deleteSource = axios.CancelToken.source();
            setCancelSource(deleteSource);

            // Saves the data, using the custom delete endpoint and payload when given
            const deleteResponse = deleteConfig
                ? await saveData({ apiUrl: `${deleteConfig.apiUrl}/${itemId}`, method: 'patch', data: deleteConfig.data ?? { deleted: true }, source: deleteSource })
                : await saveData({ apiObject, itemId, data: { deleted: true }, source: deleteSource });

            // Handle successful response
            if (deleteResponse?.status === 200) {
                setDeleteLoader(false);
                setFloatingAlert({ 'type': 'success' })

                // Close the modal before the callback so a callback which
                // opens a follow up modal is not closed right away
                handleClose();

                // Call the on success callback
                if (topModal?.props?.onSuccess) {
                    topModal.props.onSuccess(deleteResponse.data);
                };
            }
        } catch (error) {
            setDeleteLoader(false);

            // Shake the modal in any case of an error
            if (topModal?.modalRef) shakeElement(topModal.modalRef.current as HTMLElement);
        }
    }

    // Generate the field components once the data of an existing item is loaded
    const fieldComponents = useMemo(() => {
        if (!viewKey || !formDataLoaded) return;
        return generateFieldComponents(
            viewKey, 
            fields, 
            dropdownData, 
            groupOptions, 
            formData, 
            updatedData, 
            activeFeatures,
            itemId
        );
    }, [dropdownData, updatedData, formData, formDataLoaded]);

    return (
        <FormFieldContext.Provider value={{ editing, setEditing, updatedData, setUpdatedData, 
            buttonLoader, setButtonLoader, showErrorAlert, setShowErrorAlert, handleSubmit }}>
            {(errorMessages.general || showErrorAlert) &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            <form className='formset add-edit-form' 
                  onSubmit={(e) => {e.preventDefault(); handleSubmit();}}>
                {fieldComponents}

                <div className='button-row'>
                    <div className='buttons-left'>
                        {itemId && showDeleteButton && !formData?.deleted && 
                        (
                            <IconButton
                                type="button"
                                onClick={handleDelete}
                                icon={<FontAwesomeIcon icon={faTrash} />}
                                loading={deleteLoader}
                                tooltipText={deleteConfig?.tooltipText ?? 'general.delete'}
                                size="small"
                            />
                        )}
                        {extraLeftButtons}
                    </div>
                    <div className='buttons-right'>
                        <SecondaryButton
                            onClick={handleClose} 
                            label='general.cancel'
                            size="small"
                        />
                        <PrimaryButton
                            type="submit" 
                            label={primaryButtonLabel ?? 'general.save'}
                            size="small"
                            onlyViewRestriction={true}
                            loading={buttonLoader}
                        />
                    </div>
                </div>
            </form>

            {/* Show the optional side tab next to the form modal */}
            {sidePanel}
        </FormFieldContext.Provider>
    );
};

export default FormModal;